// ============================================================================
// Agentes de código — registro y presencia (#511). Paridad con CC-CONSOLE.
// ============================================================================
// Los runners viven en la PC de alguien, así que la mesa no sabe si el trabajo
// de código se está atendiendo a menos que cada uno lo diga. Esta pantalla es
// el panel de ese registro: punto verde / morado / gris y "última vez visto".
//
// Dos cosas que no son cosméticas:
//  • la llave se muestra UNA sola vez, al crearla, y no se puede recuperar
//    (el backend sólo guarda su sha256);
//  • cuando hay trabajo encolado y CERO agentes conectados, el panel lo grita:
//    es el hueco que el hospedaje local no puede evitar por sí solo.
//
// En Dumax el alta y el renombrado exigen además un APROBADOR (correo del staff
// verificado con Google, dominio autorizado): el 403 del backend dice qué
// falta y aquí se muestra tal cual. Revocar no lo exige: es el freno de
// emergencia y tiene que estar a mano.
// ============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { brand as themeBrand } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import {
  listServiceDeskAgents,
  createServiceDeskAgent,
  renameServiceDeskAgent,
  revokeServiceDeskAgent,
  formatDate,
} from "../services/serviceDeskService";
import {
  apiErrorMessage,
  presenceOf,
  PRESENCE_COLORS,
  queueCountLabel,
  queueIsStranded,
  type AgentPresence,
  type AgentsPanel,
  type ServiceDeskAgent,
} from "../services/agentsPanel";

// El latido es cada ~30 s y la ventana de "en línea" son 2 min: con 30 s el
// punto nunca va más de un latido por detrás de la realidad.
const REFRESH_MS = 30000;

// SweetAlert2 pinta `title` y `html` como HTML: todo dato del servidor que vaya
// ahí se escapa. `titleText`/`text` asignan innerText y son seguros.
const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );

export function PresenceBadge({ presence }: { presence: AgentPresence }) {
  const { t } = useTranslation("serviceDesk");
  const p = presenceOf(presence);
  const meta = PRESENCE_COLORS[p];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ color: meta.color, backgroundColor: meta.bg }}
    >
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />
      {t(`agents.presence.${p}`)}
    </span>
  );
}

export default function AgentsSection() {
  const { t } = useTranslation("serviceDesk");
  const { isDarkMode } = useTheme();
  const [panel, setPanel] = useState<AgentsPanel | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Un fallo de refresco no debe borrar lo que ya se está viendo.
  const loadedOnce = useRef(false);

  const load = useCallback(async () => {
    const data = await listServiceDeskAgents();
    setPanel(data);
    setLoadError(null);
    loadedOnce.current = true;
  }, []);

  useEffect(() => {
    load().catch((err) => setLoadError(apiErrorMessage(err) || "error"));
    const timer = setInterval(() => {
      load().catch(() => {});
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const swalTheme = { background: isDarkMode ? "#1f2937" : "#fff", color: isDarkMode ? "#fff" : "#1f2937" };
  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";

  const onCreate = async () => {
    const { value } = await Swal.fire({
      title: escapeHtml(t("agents.createDialog.title")),
      html:
        `<input id="ag-name" class="swal2-input" maxlength="60" placeholder="${escapeHtml(t("agents.createDialog.name"))}">` +
        `<input id="ag-host" class="swal2-input" maxlength="80" placeholder="${escapeHtml(t("agents.createDialog.host"))}">`,
      preConfirm: () => {
        const name = (document.getElementById("ag-name") as HTMLInputElement | null)?.value?.trim();
        const host = (document.getElementById("ag-host") as HTMLInputElement | null)?.value?.trim();
        if (!name) {
          Swal.showValidationMessage(t("agents.createDialog.nameRequired"));
          return false;
        }
        return { name, host };
      },
      showCancelButton: true,
      confirmButtonText: t("agents.createDialog.confirm"),
      cancelButtonText: t("agents.createDialog.cancel"),
      confirmButtonColor: themeBrand.primary,
      ...swalTheme,
    });
    if (!value?.name) return;

    setBusy(true);
    try {
      const created = await createServiceDeskAgent({ name: value.name, host: value.host || undefined });
      await load().catch(() => {});
      // La llave existe UNA vez. El diálogo lo dice sin rodeos y permite
      // copiarla; después de cerrarlo ya no hay forma de volver a verla.
      const r = await Swal.fire({
        icon: "success",
        title: escapeHtml(t("agents.keyDialog.title")),
        html:
          `<p style="font-size:13px;margin-bottom:8px">${escapeHtml(t("agents.keyDialog.text"))}</p>` +
          `<code data-testid="agent-key" style="display:block;word-break:break-all;background:#f3f4f6;color:#111827;padding:10px;border-radius:8px;font-size:12px">${escapeHtml(created.key)}</code>` +
          (created.keyHint
            ? `<p style="font-size:12px;margin-top:8px;opacity:.8">${escapeHtml(created.keyHint)}</p>`
            : ""),
        showCancelButton: true,
        confirmButtonText: t("agents.keyDialog.copy"),
        cancelButtonText: t("agents.keyDialog.done"),
        confirmButtonColor: themeBrand.primary,
        allowOutsideClick: false,
        ...swalTheme,
      });
      if (r.isConfirmed) await navigator.clipboard?.writeText(created.key).catch(() => {});
    } catch (err) {
      Swal.fire({ icon: "error", title: t("agents.errors.create"), text: apiErrorMessage(err), ...swalTheme });
    } finally {
      setBusy(false);
    }
  };

  const onRename = async (agent: ServiceDeskAgent) => {
    const { value } = await Swal.fire({
      title: escapeHtml(t("agents.renameDialog.title")),
      input: "text",
      inputValue: agent.name,
      inputAttributes: { maxlength: "60" },
      showCancelButton: true,
      confirmButtonText: t("agents.renameDialog.confirm"),
      cancelButtonText: t("agents.createDialog.cancel"),
      confirmButtonColor: themeBrand.primary,
      ...swalTheme,
    });
    const name = typeof value === "string" ? value.trim() : "";
    if (!name || name === agent.name) return;
    try {
      await renameServiceDeskAgent(agent.id, name);
      await load();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("agents.errors.rename"), text: apiErrorMessage(err), ...swalTheme });
    }
  };

  const onRevoke = async (agent: ServiceDeskAgent) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      // `titleText`, no `title`: el nombre lo eligió quien dio de alta el agente.
      titleText: t("agents.revokeDialog.title", { name: agent.name }),
      text: t("agents.revokeDialog.text"),
      showCancelButton: true,
      confirmButtonText: t("agents.revokeDialog.confirm"),
      cancelButtonText: t("agents.createDialog.cancel"),
      confirmButtonColor: "#dc2626",
      ...swalTheme,
    });
    if (!isConfirmed) return;
    try {
      await revokeServiceDeskAgent(agent.id);
      await load();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("agents.errors.revoke"), text: apiErrorMessage(err), ...swalTheme });
    }
  };

  if (loadError && !panel) {
    return (
      <div className="p-8 text-center" role="alert">
        <p className={`text-sm ${text}`}>{t("agents.loadError")}</p>
        {loadError !== "error" && <p className={`mt-1 text-xs ${muted}`}>{loadError}</p>}
      </div>
    );
  }
  if (!panel) return <div className="p-8 text-center text-gray-400">{t("agents.loading")}</div>;

  const agents = Array.isArray(panel.agents) ? panel.agents : [];
  const presence = panel.presence || { online: 0, working: 0, offline: 0, revoked: 0, total: 0, live: 0 };

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="max-w-3xl">
          <h1 className={`text-xl font-bold ${text}`}>{t("agents.title")}</h1>
          <p className={`text-xs ${muted}`}>{t("agents.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={onCreate}
          disabled={busy}
          className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          style={{ backgroundColor: themeBrand.primary }}
        >
          {t("agents.create")}
        </button>
      </div>

      <AgentQueueWarning warning={panel.warning} queue={panel.queue} />

      <div className="mb-5 grid gap-3 sm:grid-cols-4">
        <Stat label={t("agents.stats.live")} value={presence.live} color="#059669" card={card} muted={muted} />
        <Stat label={t("agents.stats.working")} value={presence.working} color="#7c3aed" card={card} muted={muted} />
        <Stat label={t("agents.stats.offline")} value={presence.offline} color="#6b7280" card={card} muted={muted} />
        <Stat
          label={t("agents.stats.queue")}
          value={queueCountLabel(panel.queue)}
          color={queueIsStranded(panel) ? "#dc2626" : "#2563eb"}
          card={card}
          muted={muted}
        />
      </div>

      <div className={`rounded-xl border ${card}`}>
        {agents.length === 0 ? (
          <div className="p-8 text-center">
            <p className={`text-sm ${text}`}>{t("agents.empty")}</p>
            <p className={`mt-1 text-xs ${muted}`}>{t("agents.emptyLegacy")}</p>
          </div>
        ) : (
          <ul className={`divide-y ${isDarkMode ? "divide-dark-border" : "divide-gray-200"}`}>
            {agents.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-sm font-semibold ${text}`}>{a.name}</span>
                    <PresenceBadge presence={a.presence} />
                    {a.keyPrefix && (
                      <code className={`text-[11px] ${muted}`} title={t("agents.keyPrefixTitle")}>
                        {a.keyPrefix}…
                      </code>
                    )}
                  </div>
                  <p className={`mt-0.5 text-xs ${muted}`}>
                    {a.host || t("agents.noHost")}
                    {a.version ? ` · v${a.version}` : ""} ·{" "}
                    {t("agents.lastSeen", { when: a.lastSeenAt ? formatDate(a.lastSeenAt) : t("agents.never") })}
                  </p>
                  {a.presence === "working" && a.currentJob && (a.currentJob.reference || a.currentJob.ticketId) && (
                    <p className="mt-0.5 text-xs font-medium text-purple-600">
                      {t("agents.workingOn", { ref: a.currentJob.reference || a.currentJob.ticketId })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {a.presence !== "revoked" ? (
                    <>
                      <button type="button" onClick={() => onRename(a)} className={`text-xs ${muted} hover:underline`}>
                        {t("agents.rename")}
                      </button>
                      <button type="button" onClick={() => onRevoke(a)} className="text-xs text-red-500 hover:underline">
                        {t("agents.revoke")}
                      </button>
                    </>
                  ) : (
                    <span className={`text-xs ${muted}`}>
                      {t("agents.revokedAt", { when: a.revokedAt ? formatDate(a.revokedAt) : "" })}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * El aviso del hueco: hay trabajo de código esperando y nadie escuchando. Con
 * sólo la llave global heredada (sin presencia) el backend lo baja a `info` y
 * aquí se pinta ámbar, no rojo de alarma.
 */
export function AgentQueueWarning({
  warning,
  queue,
}: {
  warning: AgentsPanel["warning"];
  queue?: AgentsPanel["queue"];
}) {
  const { t } = useTranslation("serviceDesk");
  if (!warning) return null;
  const info = warning.level === "info";
  const tone = info
    ? {
        box: "border-amber-300 bg-amber-50 dark:bg-amber-500/10",
        icon: "ri-information-line text-amber-600",
        title: "text-amber-800 dark:text-amber-300",
        body: "text-amber-800/90 dark:text-amber-300/80",
        heading: t("agents.warning.infoTitle"),
      }
    : {
        box: "border-red-300 bg-red-50 dark:bg-red-500/10",
        icon: "ri-alarm-warning-line text-red-600",
        title: "text-red-700 dark:text-red-300",
        body: "text-red-700/90 dark:text-red-300/80",
        heading: t("agents.warning.errorTitle"),
      };
  return (
    <div role="alert" className={`mb-5 flex items-start gap-3 rounded-xl border p-4 ${tone.box}`}>
      <i className={`mt-0.5 text-lg ${tone.icon}`} aria-hidden />
      <div>
        <p className={`text-sm font-semibold ${tone.title}`}>{tone.heading}</p>
        <p className={`mt-0.5 text-xs ${tone.body}`}>{warning.message}</p>
        {queue?.oldestWaitingAt && !queue.unavailable && (
          <p className={`mt-0.5 text-xs ${tone.body}`}>
            {t("agents.warning.oldest", { when: formatDate(queue.oldestWaitingAt) })}
          </p>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  color,
  card,
  muted,
}: {
  label: string;
  value: number | string;
  color: string;
  card: string;
  muted: string;
}) {
  return (
    <div className={`rounded-xl border p-4 ${card}`}>
      <p className={`text-xs ${muted}`}>{label}</p>
      <p className="text-2xl font-bold" style={{ color }}>
        {value}
      </p>
    </div>
  );
}
